import { safeErr, isValidEmail, isValidPhone, openStatus } from '../App';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLead, updateLead, sendEmail, addLeadToContact } from '../api';

const SC={sent:'s-sent',followup_1:'s-followup',followup_2:'s-followup',followup_3:'s-followup',replied:'s-replied',closed:'s-closed',new:'s-new',nurture:'s-nurture'};
const SL={sent:'Email sent',followup_1:'Follow-up 1',followup_2:'Follow-up 2',followup_3:'Follow-up 3',replied:'Replied',closed:'Closed',new:'New',nurture:'Nurture — follow-up scheduled'};
const SECTORS = ['IT & Software','Healthcare','Manufacturing','Retail','Education','Logistics','Finance','Real Estate','Food & Agri'];

// ── Edit Lead Modal ─────────────────────────────────────────────────────────
function EditModal({ lead, onClose, onSaved }) {
  const [f, setF] = useState({
    name: lead.name||'', email: lead.email||'', company: lead.company||'',
    role: lead.role||'', phone: lead.phone||'', sector: lead.sector||'', location: lead.location||'',
  });
  const [saving, setSave] = useState(false);
  const [err, setErr]     = useState('');
  const s = (k,v) => setF(p=>({...p,[k]:v}));

  const save = async () => {
    if (!f.name||!f.email) { setErr('Name and email are required'); return; }
    if (!isValidEmail(f.email)) { setErr('Enter a valid email address'); return; }
    if (!isValidPhone(f.phone)) { setErr('Enter a valid phone number'); return; }
    setSave(true); setErr('');
    try { await updateLead(lead.id, f); onSaved(); }
    catch(e) { setErr(safeErr(e,'Error saving lead')); }
    finally { setSave(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:540}}>
        <div className="modal-header">
          <span className="modal-title">Edit lead</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error">{err}</div>}
          <div className="form-row">
            <div className="form-group"><label className="form-label">Full name *</label><input className="form-input" value={f.name} onChange={e=>s('name',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={f.email} onChange={e=>s('email',e.target.value)}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={f.company} onChange={e=>s('company',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Role / Title</label><input className="form-input" value={f.role} onChange={e=>s('role',e.target.value)}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" type="tel" value={f.phone} onChange={e=>s('phone',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Sector</label>
              <select className="form-select" value={f.sector} onChange={e=>s('sector',e.target.value)}>
                <option value="">Select sector</option>
                {SECTORS.map(x=><option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={f.location} onChange={e=>s('location',e.target.value)}/></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving?'Saving…':'Save changes'}</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function LeadDetail({ showToast }) {
  const { id } = useParams();
  const nav    = useNavigate();
  const [lead, setLead]   = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSave] = useState(false);
  const [editing, setEditing]   = useState(false);
  const [sending, setSending]   = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const load = () => getLead(id).then(r => { setLead(r.data); setNotes(r.data.notes||''); });
  useEffect(() => { load(); }, [id]);

  const saveNotes = async () => {
    setSave(true);
    await updateLead(id, { notes });
    setSave(false);
    showToast('Notes saved');
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await sendEmail(parseInt(id));
      showToast('Email sent!');
      load();
    } catch (e) {
      showToast(safeErr(e, 'Failed to send email'), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleAddToContacts = async () => {
    const r = await addLeadToContact(id);
    if (r.data.already_existed) showToast('Already in contacts');
    else { showToast('Added to contacts!'); nav(`/contacts/${r.data.id}`); }
  };

  if (!lead) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  const initials = lead.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div>
      {editing && (
        <EditModal lead={lead} onClose={()=>setEditing(false)}
          onSaved={()=>{setEditing(false);showToast('Lead updated');load();}}/>
      )}

      {/* Header */}
      <div className="card">
        <div className="card-body">
          <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
            <div className="avatar av-lg av-blue" style={{flexShrink:0}}>{initials}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:20,fontWeight:700}}>{lead.name}</div>
              <div style={{fontSize:13,color:'var(--text-2)',marginTop:3}}>
                {[lead.role,lead.company,lead.sector].filter(Boolean).join(' · ')}
              </div>
              <div style={{display:'flex',gap:6,marginTop:10,flexWrap:'wrap'}}>
                <span className={`pill ${SC[lead.status]||'s-new'}`}>{SL[lead.status]||lead.status}</span>
                {lead.interest && <span className="pill pill-green">{lead.interest}</span>}
                <span style={{fontSize:12,color:'var(--text-2)'}}>{lead.email}</span>
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexShrink:0,flexWrap:'wrap'}}>
              {lead.status==='new' && (
                <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending}>
                  {sending ? <><span className="spinner spinner-sm"/> Sending…</> : '↑ Send email'}
                </button>
              )}
              <button className="btn btn-sm" onClick={()=>setEditing(true)}>✎ Edit</button>
              <button className="btn btn-success btn-sm" onClick={handleAddToContacts}>👥 Add to contacts</button>
              <button className="btn btn-sm" onClick={() => nav(`/email-manager?compose=1&to=${lead.email}&name=${encodeURIComponent(lead.name)}`)}>
                ✉ Email
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
        {[['Emails sent',lead.email_logs?.length||0],['Replies',lead.replies?.length||0],['Follow-ups',lead.followup_count||0]].map(([l,v])=>(
          <div key={l} className="metric-card" style={{marginBottom:0}}>
            <div className="metric-label">{l}</div>
            <div className="metric-value">{v}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Left column */}
        <div>
          <div className="card">
            <div className="card-header"><span className="card-title">Contact info</span></div>
            <div className="card-body">
              {[['Email',lead.email],['Phone',lead.phone],['Company',lead.company],['Role',lead.role],
                ['Sector',lead.sector],['Location',lead.location],['Source',lead.source],
                ['Added',new Date(lead.created_at).toLocaleDateString('en-IN')]].map(([l,v])=>v?(
                <div key={l} style={{display:'flex',gap:8,marginBottom:10,fontSize:13}}>
                  <span style={{color:'var(--text-2)',minWidth:72,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em',paddingTop:1}}>{l}</span>
                  <span style={{color:l==='Email'?'var(--blue)':'var(--text)'}}>{v}</span>
                </div>
              ):null)}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Notes</span></div>
            <div className="card-body">
              <textarea className="form-textarea" value={notes}
                onChange={e=>setNotes(e.target.value)}
                placeholder="Add notes about this lead…" style={{minHeight:100}}/>
              <button className="btn btn-primary btn-sm" style={{marginTop:10}}
                disabled={saving} onClick={saveNotes}>
                {saving?'Saving…':'Save notes'}
              </button>
            </div>
          </div>
        </div>

        {/* Right: timeline */}
        <div className="card" style={{marginBottom:0}}>
          <div className="card-header"><span className="card-title">Activity timeline</span></div>
          <div className="card-body">
            <div className="timeline">
              {lead.replies?.map((r,i) => {
                const interest = r.interest || lead.interest || '';
                const interestPill = interest === 'not_interested'
                  ? <span className="pill pill-red"   style={{marginLeft:6,fontSize:10}}>Not interested</span>
                  : interest === 'maybe'
                  ? <span className="pill pill-amber" style={{marginLeft:6,fontSize:10}}>Maybe</span>
                  : interest === 'interested'
                  ? <span className="pill pill-green" style={{marginLeft:6,fontSize:10}}>Interested</span>
                  : null;
                const dotColor = interest === 'not_interested' ? 'var(--red)' : interest === 'maybe' ? 'var(--amber)' : 'var(--green)';
                return (
                <div key={r.id} className="tl-item">
                  <div className="tl-left">
                    <div className="tl-dot" style={{background: dotColor}}/>
                    {i < (lead.replies.length + (lead.email_logs?.length||0) - 1) && <div className="tl-line"/>}
                  </div>
                  <div className="tl-body">
                    <div className="tl-label">Reply received {interestPill}</div>
                    <div className="tl-time">{new Date(r.received_at).toLocaleString('en-IN')}</div>
                    <div className="tl-preview">"{r.body?.slice(0,120)}…"</div>
                  </div>
                </div>
                );
              })}
              {lead.email_logs?.slice().reverse().map((log, i) => {
                const os = openStatus(log.opened_at, log.open_count);
                const isExpanded = expandedLog === log.id;
                return (
                <div key={log.id} className="tl-item">
                  <div className="tl-left">
                    <div className="tl-dot" style={{background: log.email_type==='initial'?'var(--blue)':log.email_type==='reply'?'var(--green)':'var(--amber)'}}/>
                    {i < (lead.email_logs.length-1) && <div className="tl-line"/>}
                  </div>
                  <div className="tl-body" style={{cursor:'pointer'}} onClick={()=>setExpandedLog(isExpanded?null:log.id)}>
                    <div className="tl-label">
                      {log.email_type==='initial'?'Initial email sent'
                        :log.email_type==='reply'?'Reply sent'
                        :`Follow-up ${log.email_type.replace('followup_','')} sent`}
                      <span className={`pill ${os.opened?'pill-green':'pill-gray'}`} style={{marginLeft:6,fontSize:10}} title={os.tooltip}>
                        {os.label}
                      </span>
                      <span style={{marginLeft:6,fontSize:10,color:'var(--text-3)'}}>{isExpanded?'▲':'▼'}</span>
                    </div>
                    <div className="tl-time">{new Date(log.sent_at).toLocaleString('en-IN')}</div>
                    <div className="tl-preview">"{log.subject}"</div>
                    {isExpanded && log.body && (
                      <div style={{marginTop:8,padding:'10px 12px',background:'var(--surface2)',borderRadius:'var(--r)',
                        fontSize:12,lineHeight:1.6,color:'var(--text)',whiteSpace:'pre-wrap',maxHeight:300,overflowY:'auto'}}
                        onClick={e=>e.stopPropagation()}>
                        {log.body.replace(/<[^>]+>/g,' ').trim()}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
              {!lead.email_logs?.length && !lead.replies?.length && (
                <div className="empty-state" style={{padding:24}}>
                  <div className="empty-desc">No emails sent yet</div>
                  {lead.status==='new' && <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending}>{sending?'Sending…':'Send first email'}</button>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
